import * as React from 'react';
import { Tooltip } from '@cian/ui-kit';
import {
  IconInfrastructureMuseum16,
  IconInfrastructureHospital16,
  IconInfrastructureKid16,
  IconInfrastructureSchool16,
  IconInfrastructureStore16,
} from '@cian/ui-kit-design-tokens/icons';
import classnames from 'clsx';

import * as styles from './styles.css';
import { EGenplanInfrastructureCategory, ICoordinates } from '../../../../types/newbuilding/genplan';
import { InfrastructureItemTooltip } from '../InfrastructureItemTooltip';

interface IInfrastructureItemProps {
  name: string;
  category: EGenplanInfrastructureCategory;
  tooltipPoint: ICoordinates;
  containerWidth: number;
  containerHeight: number;
  onHover(category: EGenplanInfrastructureCategory): void;
}

export function InfrastructureItem({
  name,
  category,
  tooltipPoint,
  containerWidth,
  containerHeight,
  onHover,
}: IInfrastructureItemProps) {
  const [hovered, changeHovered] = React.useState(false);

  const setHovered = React.useCallback(() => {
    changeHovered(true);
    onHover(category);
  }, [changeHovered, onHover]);
  const clearHovered = React.useCallback(() => {
    changeHovered(false);
  }, [changeHovered]);

  const icon = React.useMemo(() => {
    switch (category) {
      case EGenplanInfrastructureCategory.Attraction:
        return <IconInfrastructureMuseum16 color="icon-inverted-default" />;
      case EGenplanInfrastructureCategory.Clinic:
        return <IconInfrastructureHospital16 color="icon-inverted-default" />;
      case EGenplanInfrastructureCategory.Grocery:
        return <IconInfrastructureStore16 color="icon-inverted-default" />;
      case EGenplanInfrastructureCategory.Nursery:
        return <IconInfrastructureKid16 color="icon-inverted-default" />;
      case EGenplanInfrastructureCategory.School:
        return <IconInfrastructureSchool16 color="icon-inverted-default" />;
    }
  }, [category]);

  const relativeX = React.useMemo(() => (tooltipPoint.x / containerWidth) * 100, [containerWidth, tooltipPoint]);
  const relativeY = React.useMemo(() => (tooltipPoint.y / containerHeight) * 100, [containerHeight, tooltipPoint]);

  return (
    <div
      className={styles['pin']}
      style={{
        left: `${relativeX}%`,
        top: `${relativeY}%`,
      }}
    >
      <div className={styles['point-wrapper']} onMouseEnter={setHovered} onMouseLeave={clearHovered}>
        <Tooltip open={hovered} theme="white" title={<InfrastructureItemTooltip title={name} />}>
          <div
            className={classnames(styles['point'], hovered && styles['point-hovered'])}
            data-testid="InfrastructurePoint"
          >
            {!hovered && <div className={styles['icon']}>{icon}</div>}
          </div>
        </Tooltip>
      </div>
    </div>
  );
}
